import {
  Box,
  Typography,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
} from "@mui/material";
import C from "./colors";

/**
 * Shared ReactMarkdown component map for terminal-style rendering.
 * Used by WorkflowExecutionStreamModal and StageExecutionPanel.
 */
const terminalMarkdownComponents = {
  code: ({ node: _node, inline, className, children, ...props }) => (
    <code
      className={className}
      style={{
        backgroundColor: inline ? "rgba(0, 0, 0, 0.04)" : "#1e1e1e",
        color: inline ? C.indigo : "#e0e0e0",
        padding: inline ? "0.2rem 0.4rem" : "1rem",
        borderRadius: inline ? "4px" : "8px",
        display: inline ? "inline" : "block",
        whiteSpace: "pre-wrap",
        fontFamily: C.mono,
        fontSize: inline ? "0.85em" : "10.5px",
        marginBottom: inline ? 0 : "1em",
        border: inline ? "1px solid rgba(0,0,0,0.08)" : "none",
        lineHeight: 1.5,
      }}
      {...props}
    >
      {children}
    </code>
  ),
  p: ({ children }) => (
    <Typography
      component="div"
      sx={{
        mb: 1.5,
        fontSize: "11px",
        lineHeight: 1.7,
        color: C.sub,
        fontFamily: C.mono,
        whiteSpace: "pre-wrap",
      }}
    >
      {children}
    </Typography>
  ),
  h1: ({ children }) => (
    <Typography
      sx={{
        mt: 2,
        mb: 1,
        fontWeight: 700,
        color: C.indigo,
        fontSize: "12px",
        fontFamily: C.mono,
        textTransform: "uppercase",
        letterSpacing: "0.05em",
      }}
    >
      {children}
    </Typography>
  ),
  h2: ({ children }) => (
    <Typography
      sx={{
        mt: 1.5,
        mb: 0.75,
        fontWeight: 700,
        color: C.indigo,
        fontSize: "11.5px",
        fontFamily: C.mono,
      }}
    >
      {children}
    </Typography>
  ),
  h3: ({ children }) => (
    <Typography
      sx={{ mt: 1, mb: 0.5, fontWeight: 600, color: C.text, fontSize: "11px", fontFamily: C.mono }}
    >
      {children}
    </Typography>
  ),
  li: ({ children, ...props }) => (
    <Box
      component="li"
      sx={{
        pl: 0.5,
        mb: 0.5,
        fontSize: "11px",
        color: C.sub,
        fontFamily: C.mono,
        lineHeight: 1.6,
        display: "list-item",
        "& p": { mb: 0 },
      }}
      {...props}
    >
      {children}
    </Box>
  ),
  ul: ({ children, ...props }) => (
    <Box
      component="ul"
      sx={{
        pl: 2.5,
        mb: 1,
        listStyleType: "disc",
        fontFamily: C.mono,
        "& li": { display: "list-item" },
      }}
      {...props}
    >
      {children}
    </Box>
  ),
  ol: ({ children, ...props }) => (
    <Box
      component="ol"
      sx={{
        pl: 2.5,
        mb: 1,
        listStyleType: "decimal",
        fontFamily: C.mono,
        "& li": { display: "list-item" },
      }}
      {...props}
    >
      {children}
    </Box>
  ),
  hr: () => <Divider sx={{ my: 2, borderColor: C.border }} />,
  blockquote: ({ children }) => (
    <Box
      sx={{
        borderLeft: `3px solid ${C.indigoBdr}`,
        pl: 1.5,
        py: 0.5,
        my: 1.5,
        backgroundColor: C.indigoBg,
        borderRadius: "0 6px 6px 0",
        fontStyle: "italic",
        fontFamily: C.mono,
        fontSize: "11px",
      }}
    >
      {children}
    </Box>
  ),
  a: ({ href, children }) => (
    <Typography
      component="a"
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      sx={{
        color: C.indigo,
        textDecoration: "none",
        fontWeight: 500,
        borderBottom: "1px solid transparent",
        transition: "border-color 0.2s",
        "&:hover": { borderBottomColor: C.indigo },
      }}
    >
      {children}
    </Typography>
  ),
  strong: ({ children }) => (
    <Box component="span" sx={{ fontWeight: 700, color: C.text }}>
      {children}
    </Box>
  ),
  em: ({ children }) => (
    <Box component="span" sx={{ fontStyle: "italic", color: C.dim }}>
      {children}
    </Box>
  ),
  table: ({ children }) => (
    <Box sx={{ my: 1.5, overflowX: "auto", border: `1px solid ${C.border}`, borderRadius: 1 }}>
      <Table size="small" sx={{ borderCollapse: "collapse" }}>
        {children}
      </Table>
    </Box>
  ),
  thead: ({ children }) => <TableHead sx={{ bgcolor: C.surface }}>{children}</TableHead>,
  tbody: ({ children }) => <TableBody>{children}</TableBody>,
  tr: ({ children }) => (
    <TableRow sx={{ "&:nth-of-type(even)": { bgcolor: C.surface } }}>{children}</TableRow>
  ),
  th: ({ children }) => (
    <TableCell
      sx={{
        fontWeight: 600,
        borderBottom: `1px solid ${C.border}`,
        p: 0.75,
        fontSize: "10.5px",
        fontFamily: C.mono,
      }}
    >
      {children}
    </TableCell>
  ),
  td: ({ children }) => (
    <TableCell
      sx={{
        borderBottom: `1px solid ${C.border}`,
        p: 0.75,
        fontSize: "10.5px",
        fontFamily: C.mono,
      }}
    >
      {children}
    </TableCell>
  ),
};

export default terminalMarkdownComponents;
